import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import Checkbox from 'components/common/checkbox';
import {
  ConfigurationContainer,
  ConfigurationHeader,
  ConfigurationNumericInput,
  ConfigurationSection,
  LabelContainer,
  TooltipIcon,
} from 'components/admin/channels/config_components';
import { faInfoCircle } from '@fortawesome/pro-light-svg-icons/faInfoCircle';
import { H3 } from 'components/common/headers';
import Tooltip from 'components/lib/deprecated_tooltip';

export default function RealtimeMessagingConfig({ configuration, updateEditedConfigValue }) {
  const { t } = useTranslation();

  if (!configuration) {
    return null;
  }

  const {
    agentCapacity,
    agentMaxCapacity,
    autoAssignMessagingSessions,
    routeNextMessagingSessionIntervalSecs,
    showPreview,
  } = configuration;

  const PreviewCheckbox = showPreview ? CheckedCheckbox : Checkbox;
  const AcceptDeclineCheckbox = autoAssignMessagingSessions ? Checkbox : CheckedCheckbox;

  return (
    <div>
      <H3 data-aid="realtimeMessagingConfig-title">{t('Realtime Messaging')}</H3>
      <ConfigurationSection data-aid="realtimeMessagingConfigSection">
        <ConfigurationContainer>
          <ConfigurationHeader>
            <LabelContainer data-aid="agentCapacityLabel">{t('Standard messaging capacity per agent')}</LabelContainer>
            {renderConfigurationTooltip(t('The number of messages agents get pushed automatically'))}
          </ConfigurationHeader>
          <ConfigurationNumericInput
            data-aid="agentCapacityInput"
            minValue={0}
            onChange={value => updateEditedConfigValue({ key: 'agentCapacity', value })}
            value={agentCapacity}
          />
        </ConfigurationContainer>

        <ConfigurationContainer>
          <ConfigurationHeader>
            <LabelContainer data-aid="agentMaxCapacityLabel">
              {t('Maximum messaging capacity per agent')}
            </LabelContainer>
            {renderConfigurationTooltip(
              t(
                'The maximum number of messages an agent can work on. Agents are able to go beyond the standard capacity to this limit'
              )
            )}
          </ConfigurationHeader>
          <ConfigurationNumericInput
            data-aid="agentMaxCapacityInput"
            minValue={0}
            onChange={value => updateEditedConfigValue({ key: 'agentMaxCapacity', value })}
            value={agentMaxCapacity}
          />
        </ConfigurationContainer>

        <ConfigurationContainer>
          <ConfigurationHeader>
            <LabelContainer data-aid="routeNextMessageLabel">
              {t('Routing delay for next message (in sec)')}
            </LabelContainer>
            {renderConfigurationTooltip(
              t(
                'After accepting, declining, or ending a message, the number of seconds until the agent is routed a new message'
              )
            )}
          </ConfigurationHeader>
          <WideConfigurationNumericInput
            data-aid="routeNextIntervalInput"
            minValue={0}
            onChange={value => updateEditedConfigValue({ key: 'routeNextMessagingSessionIntervalSecs', value })}
            value={routeNextMessagingSessionIntervalSecs}
          />
        </ConfigurationContainer>

        <ConfigurationContainer>
          <ConfigurationHeader>
            <LabelContainer data-aid="showPreviewLabel">{t('Show notification preview')}</LabelContainer>
            {renderConfigurationTooltip(
              t('New inbound messages will show or hide a preview of the customer’s message in the notification banner')
            )}
          </ConfigurationHeader>
          <CheckboxContainer>
            <PreviewCheckbox
              data-aid="previewCheckbox"
              checked={showPreview}
              onChange={() => {
                updateEditedConfigValue({ key: 'showPreview', value: !showPreview });
              }}
              label={t('Show Preview')}
              value="showMessagePreview"
            />
          </CheckboxContainer>
        </ConfigurationContainer>

        <ConfigurationContainer>
          <ConfigurationHeader>
            <LabelContainer data-aid="acceptDeclineLabel">{t('Let agents accept or decline')}</LabelContainer>
            {renderConfigurationTooltip(
              t(
                'Give agents the option to accept or decline new inbound messages, instead of having work automatically assigned.'
              )
            )}
          </ConfigurationHeader>
          <CheckboxContainer>
            <AcceptDeclineCheckbox
              data-aid="acceptDeclineCheckbox"
              checked={!autoAssignMessagingSessions}
              onChange={() => {
                updateEditedConfigValue({ key: 'autoAssignMessagingSessions', value: !autoAssignMessagingSessions });
              }}
              label={t('Show Accept/Decline banner')}
              value="acceptDecline"
            />
          </CheckboxContainer>
        </ConfigurationContainer>
      </ConfigurationSection>
    </div>
  );

  function renderConfigurationTooltip(message) {
    return (
      <Tooltip message={message} position="bottom">
        <div>
          <TooltipIcon icon={faInfoCircle} />
        </div>
      </Tooltip>
    );
  }
}

const CheckboxContainer = styled.div`
  margin-left: 12px;
`;

const CheckedCheckbox = styled(Checkbox)`
  div {
    background: ${p => p.theme.colors.green400};
    border: 1px solid ${p => p.theme.colors.green400};
  }
`;

const WideConfigurationNumericInput = styled(ConfigurationNumericInput)`
  width: 80px;
`;

RealtimeMessagingConfig.propTypes = {
  configuration: PropTypes.shape({
    agentCapacity: PropTypes.number,
    agentMaxCapacity: PropTypes.number,
    autoAssignMessagingSessions: PropTypes.bool,
    routeNextMessagingSessionIntervalSecs: PropTypes.number,
    showPreview: PropTypes.bool,
  }),
  updateEditedConfigValue: PropTypes.func.isRequired,
};



// WEBPACK FOOTER //
// ./src/scripts/presentation/components/admin/channels/realtime_messaging_config.jsx